import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Button,
  VStack,
  Text,
  useToast,
  Divider,
  Box,
  Flex,
  useClipboard,
  Input,
  ButtonGroup,
  IconButton,
} from '@chakra-ui/react';
import { Copy, Check, PaperPlaneTilt } from 'phosphor-react';
import { IEnviarClienteModalProps } from '../utils/dossieTypes';
import { useDossie } from '../../../../hooks/useDossie';

// Caracteres base62 para geração de senha
const BASE62_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

// Gera senha aleatória de 10 caracteres usando base62
const generatePassword = (): string => {
  let password = '';
  for (let i = 0; i < 10; i += 1) {
    const randomIndex = Math.floor(Math.random() * BASE62_CHARS.length);
    password += BASE62_CHARS[randomIndex];
  }
  return password;
};

// Calcula a data de expiração baseado nos dias
const getExpirationDate = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

// Formata data para exibição
const formatDate = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('pt-BR');
};

export function EnviarClienteModal({ isOpen, onClose, codiEmp }: IEnviarClienteModalProps) {
  const toast = useToast();
  const { createTemporaryLink } = useDossie();

  const [selectedDays, setSelectedDays] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Estado após gerar o link
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [expiresAtFormatted, setExpiresAtFormatted] = useState<string | null>(null);

  const { hasCopied: hasCopiedLink, onCopy: onCopyLink } = useClipboard(generatedLink || '');
  const { hasCopied: hasCopiedPassword, onCopy: onCopyPassword } = useClipboard(
    generatedPassword || '',
  );

  const handleGenerateLink = async () => {
    if (!selectedDays) {
      toast({
        title: 'Selecione a validade',
        description: 'Escolha por quantos dias o link será válido',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      // Gera senha automaticamente
      const password = generatePassword();
      const expiresAtISO = getExpirationDate(selectedDays);

      const response = await createTemporaryLink({
        codi_emp: codiEmp,
        password,
        expires_at: expiresAtISO,
      });

      // Monta o link sem a senha
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}/dossie/${response.data.hashed_link}`;

      setGeneratedLink(fullLink);
      setGeneratedPassword(password);
      setExpiresAtFormatted(formatDate(selectedDays));

      toast({
        title: 'Link gerado com sucesso',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao gerar link',
        description: error?.response?.data?.detail || 'Tente novamente',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSelectedDays(null);
    setGeneratedLink(null);
    setGeneratedPassword(null);
    setExpiresAtFormatted(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={2}>
            <PaperPlaneTilt size={24} color="var(--chakra-colors-blue-500)" />
            <Text>Enviar Dossiê ao Cliente</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {!generatedLink ? (
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Validade do link</FormLabel>
                <ButtonGroup size="md" isAttached variant="outline" width="100%">
                  <Button
                    flex={1}
                    colorScheme={selectedDays === 1 ? 'blue' : 'gray'}
                    variant={selectedDays === 1 ? 'solid' : 'outline'}
                    onClick={() => setSelectedDays(1)}
                  >
                    1 dia
                  </Button>
                  <Button
                    flex={1}
                    colorScheme={selectedDays === 3 ? 'blue' : 'gray'}
                    variant={selectedDays === 3 ? 'solid' : 'outline'}
                    onClick={() => setSelectedDays(3)}
                  >
                    3 dias
                  </Button>
                  <Button
                    flex={1}
                    colorScheme={selectedDays === 7 ? 'blue' : 'gray'}
                    variant={selectedDays === 7 ? 'solid' : 'outline'}
                    onClick={() => setSelectedDays(7)}
                  >
                    7 dias
                  </Button>
                </ButtonGroup>
                {selectedDays && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Válido até: {formatDate(selectedDays)}
                  </Text>
                )}
              </FormControl>

              <Button
                colorScheme="blue"
                onClick={handleGenerateLink}
                isLoading={isLoading}
                loadingText="Gerando..."
                mt={2}
                isDisabled={!selectedDays}
              >
                Gerar Link
              </Button>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <Box bg="green.50" p={4} borderRadius="md" border="1px" borderColor="green.200">
                <Text fontSize="sm" fontWeight="bold" color="green.700" mb={2}>
                  Link gerado com sucesso!
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Copie o link e a senha abaixo e envie ao cliente.
                </Text>
              </Box>

              <Divider />

              <FormControl>
                <FormLabel fontSize="sm">Link de acesso</FormLabel>
                <Flex gap={2} align="center">
                  <Input value={generatedLink} isReadOnly fontSize="sm" flex={1} bg="gray.50" />
                  <IconButton
                    aria-label="Copiar link"
                    icon={hasCopiedLink ? <Check size={18} /> : <Copy size={18} />}
                    colorScheme={hasCopiedLink ? 'green' : 'gray'}
                    variant="ghost"
                    size="sm"
                    onClick={onCopyLink}
                  />
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Senha de acesso</FormLabel>
                <Flex gap={2} align="center">
                  <Input
                    value={generatedPassword || ''}
                    isReadOnly
                    fontSize="sm"
                    flex={1}
                    bg="gray.50"
                    fontFamily="mono"
                    fontWeight="semibold"
                  />
                  <IconButton
                    aria-label="Copiar senha"
                    icon={hasCopiedPassword ? <Check size={18} /> : <Copy size={18} />}
                    colorScheme={hasCopiedPassword ? 'green' : 'gray'}
                    variant="ghost"
                    size="sm"
                    onClick={onCopyPassword}
                  />
                </Flex>
              </FormControl>

              <Text fontSize="sm" color="gray.600">
                <strong>Válido até:</strong> {expiresAtFormatted}
              </Text>

              <Button variant="outline" onClick={handleClose} mt={2}>
                Fechar
              </Button>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
